/**
 * Sync queue manager for offline-first operations
 * Handles syncing queued writes when connectivity is restored
 */

import {
  getQueuedWrites,
  removeQueuedWrite,
  queueWrite as addToQueue,
} from './db';
import { QueuedWrite } from '@/types';

type SyncHandler = (write: QueuedWrite) => Promise<void>;

const syncHandlers: Record<string, SyncHandler> = {};

/**
 * Register a handler for a specific write type
 */
export function registerSyncHandler(type: string, handler: SyncHandler): void {
  syncHandlers[type] = handler;
}

let processing = false;

/**
 * Process all queued writes.
 * Re-entrancy guard: the queue is triggered from overlapping sources (the
 * 'online' event, the periodic interval, initial load, auth restore), and a
 * replay can take a long time on slow connections — without the guard, two
 * runs can replay the same write and double-submit a guest session.
 */
export async function processSyncQueue(): Promise<void> {
  if (processing) return;
  processing = true;

  try {
    const writes = await getQueuedWrites();

    for (const write of writes) {
      try {
        const handler = syncHandlers[write.type];
        if (!handler) {
          console.warn(`No sync handler registered for type: ${write.type}`);
          continue;
        }

        await handler(write);
        await removeQueuedWrite(write.id);
      } catch (error) {
        console.error(`Failed to sync write ${write.id}:`, error);
        // Keep in queue for retry
      }
    }
  } finally {
    processing = false;
  }
}

/**
 * Queue a write operation for later sync
 */
export async function queueWrite(
  type: 'guest' | 'photoSession',
  payload: unknown
): Promise<void> {
  const write: QueuedWrite = {
    id: `${type}-${Date.now()}-${Math.random()}`,
    type,
    payload,
    createdAt: Date.now(),
    retryCount: 0,
  };

  await addToQueue(write);
}

/**
 * Check online status and trigger sync if online
 */
export function setupAutoSync(): void {
  // Sync when coming back online
  window.addEventListener('online', () => {
    console.log('Connection restored, processing sync queue...');
    processSyncQueue();
  });

  // Periodic sync check (every 5 minutes)
  setInterval(
    () => {
      if (navigator.onLine) {
        processSyncQueue();
      }
    },
    5 * 60 * 1000
  );

  // Initial sync on load if online
  if (navigator.onLine) {
    processSyncQueue();
  }
}
